import type { SkuUsage } from '../../data/analytics';
import type { ApiDetail } from '../../features/catalog/model';
import { canChartUsage, weeklyTrend } from '../../lib/networkUsage';

/**
 * The platform's weekly request series: its endpoints' series added bucket for
 * bucket. The gateway aligns every series to the same traffic buckets, and the
 * sum is taken from the end so a shorter series lands on the newest buckets it
 * belongs to rather than being stretched.
 *
 * Returns undefined when the combined series cannot honestly carry a line -
 * exactly the condition `canChartUsage` applies to one endpoint, so the API
 * page and the endpoint page hide the spline on the same rule.
 */
export const apiUsageSeries = (
  api: ApiDetail,
  usage: Record<string, SkuUsage>,
): number[] | undefined => {
  const series = api.endpoints
    .map((e) => usage[e.sku]?.weekly)
    .filter((w): w is number[] => Array.isArray(w) && w.length > 0);
  if (series.length === 0) return undefined;

  const width = Math.max(...series.map((w) => w.length));
  const total = new Array<number>(width).fill(0);
  for (const w of series) {
    const offset = width - w.length;
    w.forEach((v, i) => {
      total[offset + i] += v;
    });
  }

  const requests = total.reduce((a, b) => a + b, 0);
  const combined = { requests, weekly: total, trendPct: weeklyTrend(total)?.pct ?? null };
  return canChartUsage(combined) ? total : undefined;
};
